<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import SignIn from '@/components/SignIn.vue'
import { api, ApiError, signedIn, signOut } from '@/lib/api'
import { setTheme, theme, type ThemeChoice } from '@/lib/theme'

interface Me { user: { email: string; name: string }; lastRefresh: string | null; salesStart: string }
const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/me'), retry: false })
watch(() => me.data.value, (d) => { if (d) signedIn.value = true })
// Only a 401 means signed out (api() sets signedIn then). Any other failure is shown, not mistaken for one:
// otherwise a server error after sign-in sends you straight back to the sign-in screen.
const loadError = computed(() => {
  const e = me.error.value
  return e && !(e instanceof ApiError && e.status === 401) ? e.message : ''
})

const route = useRoute()
const router = useRouter()
const search = ref('')
const nav: [string, string][] = [['/', 'Overview'], ['/campaigns', 'Campaigns'], ['/leads/performance', 'Leads'], ['/paid', 'Paid ads'], ['/contacts', 'Journeys'], ['/management', 'Management']]
// A section is active on any of its pages: Leads links to /leads/performance but also covers /leads and /leads/checks.
const active = (p: string) => (p === '/' ? route.path === '/' : route.path.startsWith(p.split('/').slice(0, 2).join('/')))
const refreshed = computed(() => me.data.value?.lastRefresh?.slice(0, 16).replace('T', ' '))
function go() { router.push({ path: '/contacts', query: { q: search.value } }) }
async function onSignedIn() { await me.refetch() }
</script>

<template>
  <SignIn v-if="signedIn === false" @signed-in="onSignedIn" />
  <div v-else-if="loadError" class="min-h-screen flex items-center justify-center">
    <div class="card text-center p-8 max-w-sm">
      <div class="font-bold text-brand text-lg mb-1">Outreach Insights</div>
      <p class="text-muted mb-4">{{ loadError }}</p>
      <button class="btn" @click="me.refetch()">Try again</button>
    </div>
  </div>
  <template v-else-if="signedIn">
    <header class="bg-surface border-b border-line sticky top-0 z-10">
      <div class="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center gap-x-6 gap-y-1 flex-wrap">
        <RouterLink to="/" class="font-bold text-brand text-base hover:no-underline py-3">Outreach Insights</RouterLink>
        <!-- Scrolls sideways on a phone instead of overflowing; the active page is underlined. -->
        <nav class="order-last sm:order-none w-full sm:w-auto flex gap-4 overflow-x-auto [scrollbar-width:none] -mb-px">
          <RouterLink v-for="[p, l] in nav" :key="p" :to="p" :aria-current="active(p) ? 'page' : undefined"
            :class="['whitespace-nowrap py-3 border-b-2 hover:no-underline', active(p) ? 'border-brand text-ink font-semibold' : 'border-transparent text-muted hover:text-ink']">{{ l }}</RouterLink>
        </nav>
        <form class="ml-auto hidden md:block" role="search" @submit.prevent="go">
          <input v-model="search" class="field w-56" placeholder="Find a contact" aria-label="Find a contact" /></form>
        <select class="field !py-0.5 !text-[12px] text-muted ml-auto md:ml-0" aria-label="Colour theme" :value="theme"
          @change="setTheme(($event.target as HTMLSelectElement).value as ThemeChoice)">
          <option value="system">System theme</option><option value="light">Light</option><option value="dark">Dark</option>
        </select>
        <span class="text-muted text-[13px]">{{ me.data.value?.user.name }}
          <a href="#" class="ml-2" @click.prevent="signOut">Sign out</a></span>
      </div>
    </header>
    <main class="px-4 sm:px-6 pt-6 pb-10 max-w-[1400px] mx-auto">
      <div v-if="me.data.value && !me.data.value.lastRefresh" class="bg-wash rounded-lg px-3 py-2.5 mb-4">
        No data has been published yet: the first refresh is still running. Pages fill in once it finishes.
      </div>
      <RouterView />
    </main>
    <footer class="text-muted text-xs px-4 sm:px-6 pb-8 max-w-[1400px] mx-auto">
      Data refreshed {{ refreshed ? refreshed + ' UTC' : 'never' }}. Sales data from {{ me.data.value?.salesStart }}
      (HubSpot sales migration, March 2025). Outreach metrics are prospects only: partners, distributors, vendors and
      existing customers are left out.
    </footer>
  </template>
</template>
